import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { watchPath, listMembers, logActivity, updateActivity, callFields, saveRecord, DISPOSITIONS, noCalls, recordOptOut, recordAskedNotToCall, isNotInterested, isPaused, formatPhoneDash } from '../../services/crmService';
import { useVisibleContacts, useOwned } from './useCrm';
import ReengageModal from './ReengageModal';
import CallbackModal from './CallbackModal';
import { telHref, useCallingApp, canUseQuo } from '../../components/CallingAppSetting';
import { CallTallies, SessionTimer, DialerContactPanel, FollowUpText, removeBadNumber } from './DialerExtras';
import { usePipelineRows, matchPipeline } from './ContactExtras';
import AppointmentModal from './AppointmentModal';
import { ui } from './crmUi';
import { applyMerge, insertAtCursor, InsertFieldMenu } from './merge';
import { useTwilioVoice, AUTO_DIAL_COUNTDOWN_SECS, fmtDuration } from './TwilioDialer';

// Power Dialer. Default: Call opens a tel: link in the device's calling app (one click per call, never auto-dials).
// Auto-dialer (Twilio Voice, only when TWILIO_VOICE_ENABLED is on and credentials exist): calls from the browser,
// rings ~4 times (25s), logs the outcome, then dials the next eligible contact after a 3s countdown with Pause.
// DNC, opted-out, paused, archived and no-phone contacts are skipped.
export default function PowerDialerPage() {
  const { user, userRole } = useAuth();
  const [reengageFor, setReengageFor] = useState(null);
  const [callbackFor, setCallbackFor] = useState(null);
  const [apptFor, setApptFor] = useState(null);
  const [lists, setLists] = useState([]);
  const contacts = useVisibleContacts();
  const [listId, setListId] = useState(() => new URLSearchParams(window.location.search).get('list') || '');
  const [index, setIndex] = useState(0);
  const [calling, setCalling] = useState(false);
  const callingApp = useCallingApp(user?.uid);
  const pipeRows = usePipelineRows();
  const [sessionCalls, setSessionCalls] = useState(0);
  const [sessionBad, setSessionBad] = useState(0);
  const [followUp, setFollowUp] = useState(null);
  const [autoSecs, setAutoSecs] = useState('');
  const [confirmBad, setConfirmBad] = useState(false);
  const [note, setNote] = useState('');
  const [log, setLog] = useState([]);
  // ---- Twilio auto-dialer session
  const voice = useTwilioVoice(user?.uid);
  const [autoOn, setAutoOn] = useState(false); // session running
  const [autoPaused, setAutoPaused] = useState(false);
  const [countdown, setCountdown] = useState(null); // seconds left before the next auto-dial
  const [awaitOutcome, setAwaitOutcome] = useState(false); // answered call ended: agent picks the outcome
  const [lastCall, setLastCall] = useState(null); // { answered, durationSecs }
  const [, setTick] = useState(0); // re-render each second for the live call timer

  const own_Lists = useOwned('crm/lists');
  useEffect(() => setLists(own_Lists), [own_Lists]);
  // Call scripts (crm/scripts): merge fields render live for the contact being dialed.
  const [scripts, setScripts] = useState([]);
  const [scriptId, setScriptId] = useState('');
  const [editingScript, setEditingScript] = useState(null);
  const scriptRef = useRef(null);
  const own_Scripts = useOwned('crm/scripts', { scoped: true });
  useEffect(() => setScripts(own_Scripts), [own_Scripts]);
  const script = scripts.find((x) => x.id === scriptId);
  const agent = { displayName: user?.displayName || '' };
  const saveScript = async () => {
    if (!editingScript.name.trim()) return;
    const { id, createdAt, updatedAt, createdBy, ...data } = editingScript;
    // New scripts: admins make team scripts (everyone reads, only admins edit); others make their own.
    const scope = id ? data.scope : (userRole === 'Admin' ? 'team' : 'personal');
    const newId = await saveRecord('crm/scripts', id, { ...data, ...(scope ? { scope } : {}), name: data.name.trim() }, user?.uid);
    setScriptId(newId); setEditingScript(null);
  };

  const list = lists.find((x) => x.id === listId);
  const all = listMembers(list, contacts);
  const queue = all.filter((c) => !noCalls(c) && c.phone && !isPaused(c)); // skips DNC, opted out of calls, paused / not interested
  const skipped = all.length - queue.length;
  const current = queue[index];

  const [attemptId, setAttemptId] = useState(null);
  const next = () => { setAttemptId(null); setIndex((i) => i + 1); setCalling(false); setNote(''); setAwaitOutcome(false); setLastCall(null); };
  const durExtra = (lc) => (lc && lc.durationSecs ? { durationSecs: Math.round(lc.durationSecs) } : {});
  const durText = (lc) => (lc ? ` (${lc.answered ? fmtDuration(lc.durationSecs) : 'no answer after ~4 rings'}, Resi calling)` : '');
  const queueNextDial = () => { if (autoOn) setCountdown(AUTO_DIAL_COUNTDOWN_SECS); };
  // Twilio: place the call for the current contact; when it ends, log it (no answer auto-logs) and queue the next.
  const twilioDial = (c) => {
    if (!c || voice.status !== 'ready') return;
    setCountdown(null); setCalling(true); setAwaitOutcome(false); setLastCall(null);
    voice.dial(c, async ({ answered, durationSecs, reason }) => {
      const lc = { answered, durationSecs };
      setLastCall(lc);
      if (!answered) {
        const why = reason === 'error' ? 'Call failed' : 'No answer';
        try { await logActivity(c.id, { type: 'call', disposition: 'No answer', via: 'twilio', ...callFields(c.phone, user), durationSecs: 0, text: `Call: ${why}${durText(lc)}`, listId }, user?.uid); setSessionCalls((n) => n + 1); } catch (err) { console.error('Could not log call', err); }
        setLog((l) => [{ name: c.name, disposition: why }, ...l]);
        setIndex((i) => i + 1); setCalling(false); setNote(''); setLastCall(null);
        queueNextDial();
      } else {
        // Answered: log it now with the duration, so it is on the record even if no outcome is picked.
        try {
          const logId = await logActivity(c.id, { type: 'call', disposition: 'Connected', via: 'twilio', ...callFields(c.phone, user), ...durExtra(lc), text: `Call: Connected${durText(lc)}`, listId }, user?.uid);
          setLastCall({ ...lc, logId });
        } catch (err) { console.error('Could not log call', err); }
        setAwaitOutcome(true);
      } // talked: the agent picks the outcome, then the next call is queued
    });
  };
  const stopSession = () => { setAutoOn(false); setAutoPaused(false); setCountdown(null); voice.hangup(); };

  const dispose = async (disposition) => {
    if (!current) return;
    const viaTwilio = !!lastCall;
    if (viaTwilio && voice.status === 'in-call') voice.hangup();
    const text = `Call: ${disposition}${durText(lastCall)}${note.trim() ? `. ${note.trim()}` : ''}`;
    try {
      const entry = { type: 'call', disposition, notes: note.trim(), text, listId, ...callFields(current.phone, user), ...(viaTwilio ? { via: 'twilio', ...durExtra(lastCall) } : { via: 'tel' }) };
      // Finish the entry already written for this call (Twilio auto-log or tel: "Call started") instead of adding a second one.
      const existingId = (lastCall && lastCall.logId) || attemptId;
      if (existingId) await updateActivity(current.id, existingId, entry); else await logActivity(current.id, entry, user?.uid);
      setAttemptId(null);
      setSessionCalls((n) => n + 1);
      setFollowUp(current);
      if (disposition === 'Asked not to call') {
        await recordAskedNotToCall(current.id, 'Power Dialer', note.trim(), user?.uid);
        setLog((l) => [{ name: current.name, disposition }, ...l]);
        setCalling(false); setNote(''); setAwaitOutcome(false); setLastCall(null); queueNextDial(); // they drop out of the queue, so the index already points at the next contact
        return;
      }
      if (isNotInterested(disposition)) setReengageFor(current);
      if (disposition === 'Call back') setCallbackFor(current);
      if (disposition === 'Appointment set') setApptFor(current);
      if (disposition === 'Bad number') { await removeBadNumber(current, user?.displayName || user?.email, user?.uid); setSessionBad((n) => n + 1); }
    } catch (err) { console.error('Could not log call', err); }
    setLog((l) => [{ name: current.name, disposition }, ...l]);
    next();
    queueNextDial();
  };
  // Optional: move to the next card after X seconds with no outcome (it never dials by itself).
  useEffect(() => {
    const secs = Number(autoSecs); if (!secs || !current || !calling || autoOn) return undefined;
    const t = setTimeout(() => { next(); }, secs * 1000);
    return () => clearTimeout(t);
  }, [autoSecs, current?.id, calling]); // eslint-disable-line react-hooks/exhaustive-deps
  // Countdown to the next auto-dial (visible, pausable). Modals (callback / appointment / re-engage) hold it.
  const modalOpen = !!(reengageFor || callbackFor || apptFor);
  useEffect(() => {
    if (countdown == null || !autoOn || autoPaused || modalOpen) return undefined;
    if (!current) { setCountdown(null); setAutoOn(false); return undefined; }
    if (countdown <= 0) { setCountdown(null); twilioDial(current); return undefined; }
    const t = setTimeout(() => setCountdown((n) => (n == null ? n : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, autoOn, autoPaused, modalOpen, current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Live call timer.
  useEffect(() => { if (!voice.startedAt) return undefined; const t = setInterval(() => setTick((n) => n + 1), 1000); return () => clearInterval(t); }, [voice.startedAt]);
  const twilioMode = voice.enabled;
  const onCall = voice.status === 'ringing' || voice.status === 'in-call';
  const badNumber = async () => {
    if (!current) return;
    if (!confirmBad) { setConfirmBad(true); setTimeout(() => setConfirmBad(false), 5000); return; }
    setConfirmBad(false);
    try { await logActivity(current.id, { type: 'call', disposition: 'Bad number', notes: note.trim(), text: 'Call: Bad number', listId }, user?.uid); setSessionCalls((n) => n + 1); await removeBadNumber(current, user?.displayName || user?.email, user?.uid); setSessionBad((n) => n + 1); setLog((l) => [{ name: current.name, disposition: 'Bad number removed' }, ...l]); } catch (e) { console.error('Could not remove number', e); }
    setCalling(false); setNote(''); // without a phone they drop out of the queue
  };
  const markDnc = async () => {
    if (!current || !window.confirm(`Mark ${current.name} as Do Not Call?`)) return;
    await recordOptOut(current.id, 'dnc', 'Power Dialer', '', user?.uid);
    setCalling(false); setNote(''); // the contact drops out of the queue, so the index now points at the next one
  };

  return (
    <div style={ui.page}>
      <ReengageModal contact={reengageFor} uid={user?.uid} isAdmin={userRole === 'Admin'} onDone={() => setReengageFor(null)} />
      <CallbackModal contact={callbackFor} uid={user?.uid} isAdmin={userRole === 'Admin'} onDone={() => setCallbackFor(null)} />
      <AppointmentModal contact={apptFor} uid={user?.uid} isAdmin={userRole === 'Admin'} onDone={() => setApptFor(null)} />
      <h1 style={ui.h1}>Power Dialer</h1>
      <p style={ui.sub}>Pick a list and work through it. Every call is logged to the contact's timeline.</p>
      {twilioMode ? <div style={ui.notice}>Calls go out from Resi{voice.callerId ? ` on ${formatPhoneDash(voice.callerId)}` : ''}. Each call rings about 4 times, then hangs up. Start auto-dial and Resi logs each call and dials the next contact after a {AUTO_DIAL_COUNTDOWN_SECS}-second countdown. Pause or stop any time. Do Not Call, opted-out, archived, paused contacts and anyone without a phone number are skipped.</div> : <div style={ui.notice}>Call opens {callingApp === 'quo' ? 'Quo' : "your device's calling app"} with the number filled in (change it in My Profile). Do Not Call, opted-out, archived contacts and anyone without a phone number are skipped.</div>}
      <div style={{ ...ui.row, marginBottom: 14 }}>
        <select style={{ ...ui.input, width: '100%', maxWidth: 280 }} value={listId} onChange={(e) => { stopSession(); setListId(e.target.value); setIndex(0); setCalling(false); setLog([]); }}>
          <option value="">Choose a list...</option>
          {lists.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        {list && <span style={{ fontSize: 13 }}>{queue.length} to call{skipped ? `, ${skipped} skipped (DNC, paused, or no phone)` : ''}</span>}
      </div>
      <div style={{ ...ui.row, marginBottom: 14 }}>
        <select style={{ ...ui.input, width: '100%', maxWidth: 280 }} value={scriptId} onChange={(e) => setScriptId(e.target.value)}>
          <option value="">No call script</option>
          {scripts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <button style={ui.ghost} onClick={() => setEditingScript(script ? { ...script } : { name: '', body: '' })}>{script ? 'Edit script' : 'New script'}</button>
        {script && <button style={ui.ghost} onClick={() => setEditingScript({ name: '', body: '' })}>New script</button>}
      </div>
      {editingScript && (
        <div style={ui.card}>
          <label style={ui.label}>Script name</label>
          <input style={ui.input} value={editingScript.name} onChange={(e) => setEditingScript({ ...editingScript, name: e.target.value })} />
          <label style={ui.label}>Script</label>
          <textarea ref={scriptRef} style={{ ...ui.input, minHeight: 140 }} value={editingScript.body} onChange={(e) => setEditingScript({ ...editingScript, body: e.target.value })}
            placeholder="Hi {{first_name}}, this is {{agent_name}} with RESIDENCE. Are you still looking in {{interested_city|Southwest Florida}}?" />
          <div style={{ ...ui.row, marginTop: 8 }}>
            <InsertFieldMenu style={{ ...ui.input, width: 'auto', marginTop: 0 }} onInsert={(tok) => setEditingScript((sc) => ({ ...sc, body: insertAtCursor(scriptRef.current, sc.body, tok) }))} />
            <button style={ui.btn} disabled={!editingScript.name.trim()} onClick={saveScript}>Save script</button>
            <button style={ui.ghost} onClick={() => setEditingScript(null)}>Cancel</button>
          </div>
        </div>
      )}
      <SessionTimer />
      <CallTallies uid={user?.uid} sessionCalls={sessionCalls} sessionBad={sessionBad} refreshKey={sessionCalls} />
      {!twilioMode && <label style={{ fontSize: 13 }}>Auto-advance after <input type="number" min="0" style={{ width: 60 }} value={autoSecs} onChange={(e) => setAutoSecs(e.target.value)} /> seconds with no outcome (blank = off; it only moves to the next card, it never dials)</label>}
      {twilioMode && list && (
        <div style={{ ...ui.card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {!autoOn
            ? <button style={ui.navyBtn} disabled={!current || onCall} onClick={() => { setAutoOn(true); setAutoPaused(false); if (!onCall && !awaitOutcome) twilioDial(current); }}>Start auto-dial</button>
            : <>
                <strong>{autoPaused ? 'Auto-dial paused' : countdown != null ? `Calling next in ${countdown}...` : onCall ? 'Auto-dial on' : awaitOutcome ? 'Pick the outcome to continue' : 'Auto-dial on'}</strong>
                <button style={ui.ghost} onClick={() => setAutoPaused((x) => !x)}>{autoPaused ? 'Resume' : 'Pause'}</button>
                <button style={{ ...ui.ghost, borderColor: '#D64545', color: '#D64545' }} onClick={stopSession}>Stop session</button>
              </>}
          {voice.error && <span style={{ fontSize: 13, color: '#D64545' }}>{voice.error}</span>}
        </div>
      )}
      {followUp && <FollowUpText key={followUp.id} contact={followUp} agent={agent} isQuoUser={canUseQuo(user)} uid={user?.uid} onClose={() => setFollowUp(null)} />}
      {list && !current && <div style={ui.card}><strong>All done with this list.</strong> {queue.length ? <button style={{ ...ui.ghost, marginLeft: 10 }} onClick={() => setIndex(0)}>Start over</button> : null}</div>}
      {current && (
        <div style={ui.card}>
          <div style={{ fontSize: 12, color: '#666666' }}>Contact {index + 1} of {queue.length}</div>
          <h2 style={{ margin: '4px 0' }}>{current.name}</h2>
          <div style={{ fontSize: 15 }}>{formatPhoneDash(current.phone)}</div>
          <DialerContactPanel contact={current} pipeline={matchPipeline(current, pipeRows)} />
          {current.notes && <div style={{ fontSize: 13, color: '#666666', marginTop: 4, whiteSpace: 'pre-wrap' }}>{current.notes}</div>}
          {script && <div style={{ marginTop: 12, padding: 12, background: '#FBF7EC', border: '1px solid #d8c9a8', borderRadius: 6, whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.5 }}>{applyMerge(script.body, current, agent)}</div>}
          <div style={{ ...ui.row, margin: '14px 0' }}>
            {twilioMode
              ? (onCall
                ? <button style={{ ...ui.navyBtn, padding: '12px 22px', fontSize: 15, background: '#D64545' }} onClick={voice.hangup}>{voice.status === 'ringing' ? 'Ringing... Hang up' : `Hang up (${fmtDuration((Date.now() - voice.startedAt) / 1000)})`}</button>
                : <button style={{ ...ui.navyBtn, padding: '12px 22px', fontSize: 15 }} disabled={voice.status !== 'ready' || awaitOutcome} onClick={() => twilioDial(current)}>{awaitOutcome ? `Call ended${lastCall ? ` (${fmtDuration(lastCall.durationSecs)})` : ''}: pick the outcome below` : 'Call'}</button>)
              : <a href={telHref(current.phone) || undefined} onClick={(e) => { if (!telHref(current.phone)) { e.preventDefault(); return; } if (!calling) logActivity(current.id, { type: 'call_attempt', via: 'tel', ...callFields(current.phone, user), text: `Call started from Power Dialer (${callingApp === 'quo' ? 'Quo' : 'device calling app'})`, listId }, user?.uid).then(setAttemptId).catch(() => {}); setCalling(true); }} style={{ ...ui.navyBtn, padding: '12px 22px', fontSize: 15, textDecoration: 'none', display: 'inline-block' }}>{calling ? 'On call: pick the outcome below' : 'Call'}</a>}
            {twilioMode && <button style={ui.ghost} disabled title="Coming soon">Drop voicemail (soon)</button>}
            <button style={ui.ghost} disabled={onCall} onClick={() => { next(); queueNextDial(); }}>Skip</button>
            <button style={{ ...ui.ghost, borderColor: '#8C5A00', color: '#8C5A00' }} onClick={badNumber}>{confirmBad ? 'Tap again to remove this number' : 'Bad number'}</button>
            <button style={{ ...ui.ghost, borderColor: '#D64545', color: '#D64545' }} onClick={markDnc}>Do Not Call</button>
          </div>
          <label style={ui.label}>Notes</label>
          <textarea style={{ ...ui.input, minHeight: 60 }} value={note} onChange={(e) => setNote(e.target.value)} />
          <label style={ui.label}>How did it go?</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 8 }}>
            {DISPOSITIONS.map((d) => <button key={d} style={{ ...ui.btn, ...(d === 'Appointment set' ? {} : { background: '#FFFFFF', boxShadow: 'none', textShadow: 'none', border: '1px solid #d8c9a8' }) }} onClick={() => dispose(d)}>{d}</button>)}
          </div>
        </div>
      )}
      {log.length > 0 && (
        <div style={ui.card}><strong>This session</strong>{log.map((x, i) => <div key={i} style={{ fontSize: 13 }}>{x.name}: {x.disposition}</div>)}</div>
      )}
    </div>
  );
}
